<template>
  <div class="job-openings">
    <hero-section :title="title" :showWhiteWave="showWhiteWave" />
    <div class="section__container">
      <div class="loader" v-if="UIController.loader.contentLoading">
        <v-progress-circular indeterminate></v-progress-circular>
        <p class="mt-5">Hold On! while we fetch some details.</p>
      </div>
      <div v-else>
        <v-card
          elevation="0"
          class="error-card"
          v-if="
            UIController.displayController.hasQueryVariable &&
            !applicationData.role
          "
        >
          <v-card-title class="error-card__title text-left dms700"
            >This Job Opening is Not Available</v-card-title
          >
          <v-card-text>
            <h3 class="error-card__text-title text-left m500">
              Hmm! Looks like the job opening you are trying to access does not
              exist.
            </h3>
            <p class="error-card__text text-left m500">
              You can always look through other job openings available right
              now.
            </p>
          </v-card-text>
        </v-card>
        <div v-else class="form__container">
          <form
            class="job-application"
            method="POST"
            ref="applicationForm"
            @submit.prevent="validateForm"
          >
            <p class="form-title text-left" v-if="applicationData.role">
              Application for {{ applicationData.role }}
            </p>

            <v-text-field
              label="Full Name *"
              name="name"
              aria-label="Name"
              class="input-field m500 mb-3"
              variant="outlined"
              v-model="applicationData.name"
              :error="applicationError.name"
              :error-messages="applicationErrorMessage.name"
              aria-required="true"
            ></v-text-field>
            <v-text-field
              label="Email *"
              name="email"
              aria-label="Email"
              class="input-field m500 mb-3"
              variant="outlined"
              v-model="applicationData.email"
              :error="applicationError.email"
              :error-messages="applicationErrorMessage.email"
              aria-required="true"
              type="email"
              hint="example@mail.com"
            ></v-text-field>
            <v-text-field
              label="Phone Number *"
              aria-label="Phone Number"
              class="input-field m500 mb-3"
              variant="outlined"
              name="phone"
              prefix="+977"
              v-model="applicationData.phone"
              :error="applicationError.phone"
              :error-messages="applicationErrorMessage.phone"
              aria-required="true"
              hint="With Country Code, +9779801234567"
            ></v-text-field>
            <v-text-field
              label="Location"
              aria-label="Location"
              class="input-field m500 mb-3"
              variant="outlined"
              name="location"
              v-model="applicationData.location"
              :error="applicationError.location"
              :error-messages="applicationErrorMessage.location"
              aria-required="true"
              hint="Your current city, province"
            ></v-text-field>
            <v-text-field
              label="GitHub Username"
              aria-label="GitHub Username"
              class="input-field m500 mb-3"
              variant="outlined"
              name="github"
              prefix="github.com/"
              v-model="applicationData.github"
              :error="applicationError.github"
              :error-messages="applicationErrorMessage.github"
              aria-required="true"
              hint="github.com/username"
            ></v-text-field>
            <v-text-field
              label="Website/portfolio Link"
              aria-label="portfolio Link"
              class="input-field m500 mb-3"
              variant="outlined"
              name="portfolio"
              v-model="applicationData.portfolio"
              :error="applicationError.portfolio"
              :error-messages="applicationErrorMessage.portfolio"
              aria-required="true"
            ></v-text-field>
            <v-text-field
              label="Linkedin Username *"
              aria-label="Linkedin Username"
              class="input-field m500 mb-3"
              variant="outlined"
              name="linkedin"
              prefix="linkedin.com/in/"
              v-model="applicationData.linkedin"
              :error="applicationError.linkedin"
              :error-messages="applicationErrorMessage.linkedin"
              aria-required="true"
              hint="linkedin.com/in/username"
            ></v-text-field>
            <v-autocomplete
              label="Languages"
              multiple
              chips
              aria-label="Languages"
              class="input-field m500 mb-3"
              variant="outlined"
              :items="languages"
              name="languages"
              item-title="label"
              item-value="id"
              v-model="applicationData.languages"
              :error="applicationError.languages"
              :error-messages="applicationErrorMessage.languages"
            ></v-autocomplete>
            <v-autocomplete
              label="Frameworks"
              multiple
              chips
              aria-label="Frameworks"
              class="input-field m500 mb-3"
              variant="outlined"
              :items="frameworks"
              name="frameworks"
              item-title="label"
              item-value="id"
              v-model="applicationData.frameworks"
              :error="applicationError.frameworks"
              :error-messages="applicationErrorMessage.frameworks"
            ></v-autocomplete>
            <v-autocomplete
              label="Softwares"
              multiple
              chips
              aria-label="Softwares"
              class="input-field m500 mb-3"
              variant="outlined"
              :items="softwares"
              name="softwares"
              item-title="label"
              item-value="id"
              v-model="applicationData.softwares"
              :error="applicationError.softwares"
              :error-messages="applicationErrorMessage.softwares"
            ></v-autocomplete>
            <v-autocomplete
              label="Certifications"
              multiple
              chips
              aria-label="certifications"
              class="input-field m500 mb-3"
              variant="outlined"
              :items="certifications"
              name="certifications"
              item-title="label"
              item-value="id"
              v-model="applicationData.certifications"
              :error="applicationError.certifications"
              :error-messages="applicationErrorMessage.certifications"
            ></v-autocomplete>
            <v-textarea
              label="Message to the Hiring Manager"
              aria-label="Message to the Hiring Manager"
              class="input-field m500 mb-3"
              variant="outlined"
              name="message"
              v-model="applicationData.message"
              :error="applicationError.message"
              :error-messages="applicationErrorMessage.message"
              aria-required="true"
              no-resize
            ></v-textarea>
            <v-file-input
              label="Resume *"
              aria-label="Resume"
              class="input-field m500 mb-3"
              variant="outlined"
              name="cv"
              chips
              accept="application/pdf"
              persistent-hint
              hint="Max Upload Size: 5MB"
              v-on:change="onFileChange"
              v-model="applicationData.cv"
              :error="applicationError.cv"
              :error-messages="applicationErrorMessage.cv"
              aria-required="true"
            ></v-file-input>
            <hcaptcha
              classList="my-4 text-left"
              @verify="onVerify"
              @expired="onExpired"
              @closed="onClosed"
              @reset="onReset"
            />
            <p class="text-left">
              <v-btn
                type="submit"
                class="form-btn"
                size="large"
                ref="pageTop"
                elevation="0"
                :loading="submitting"
                :disabled="submitting"
                >{{ submitting ? 'Submitting...' : 'Submit' }}</v-btn
              >
            </p>
          </form>
        </div>
      </div>
    </div>
    <v-snackbar
      color="red-darken-2"
      v-model="UIController.snackbars.captchaSnackBar"
      >Please Verify The Captcha</v-snackbar
    >
  </div>
</template>

<script>
import HeroSectionVue from '@/components/partials/HeroSection.vue';
import jobOpeningService from '@/services/API/jobOpeningService';
import utilService from '@/services/API/utilService';
import applicationService from '@/services/API/applicationService';
import hcaptchaWidget from '@/components/partials/hcaptchaWidget.vue';

export default {
  name: 'JobApplication',
  components: {
    'hero-section': HeroSectionVue,
    hcaptcha: hcaptchaWidget,
  },
  data: () => ({
    title: 'Drop Your Information',
    submitting: false,
    showWhiteWave: true,
    applicationData: {
      role: null,
      name: null,
      email: null,
      phone: null,
      location: null,
      github: null,
      portfolio: null,
      linkedin: null,
      languages: [],
      frameworks: [],
      softwares: [],
      certifications: [],
      message: null,
      cv: [], // Initialize as an array
      verified: false,
    },
    applicationError: {
      name: false,
      email: false,
      phone: false,
      location: false,
      github: false,
      portfolio: false,
      linkedin: false,
      languages: false,
      frameworks: false,
      softwares: false,
      certifications: false,
      message: false,
      cv: false,
      verified: false,
    },
    applicationErrorMessage: {
      name: new Array(),
      email: new Array(),
      phone: new Array(),
      location: new Array(),
      github: new Array(),
      portfolio: new Array(),
      linkedin: new Array(),
      languages: new Array(),
      frameworks: new Array(),
      softwares: new Array(),
      certifications: new Array(),
      message: new Array(),
      cv: new Array(),
    },
    languages: [],
    frameworks: [],
    softwares: [],
    certifications: [],
    UIController: {
      loader: {
        contentLoading: true,
      },
      displayController: {
        hasQueryVariable: false,
      },
      snackbars: {
        captchaSnackBar: false,
        successSnackBar: false,
        errorSnackBar: false,
      },
    },
  }),
  methods: {
    onFileChange(event) {
      // Clear previous errors
      this.applicationError.cv = false;
      this.applicationErrorMessage.cv = [];
      
      // Convert FileList to an array of File objects for Vuetify
      this.applicationData.cv = Array.from(event.target.files);
      
      // Validate file size (5MB = 5 * 1024 * 1024 bytes)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      
      if (this.applicationData.cv.length > 0) {
        const file = this.applicationData.cv[0];
        if (file.size > maxSize) {
          this.applicationError.cv = true;
          this.applicationErrorMessage.cv.push('File size must be less than 5MB');
          this.applicationData.cv = []; // Clear the file
        }
      }
      
      console.log('File Selected:', this.applicationData.cv);
    },
    onVerify() {
      this.applicationData.verified = true;
    },
    onExpired() {
      this.applicationData.verified = false;
    },
    onClosed() {
      this.applicationData.verified = false;
    },
    onReset() {
      this.applicationData.verified = false;
    },
    async submitForm() {
      this.submitting = true;
      if (this.applicationData.github !== '') {
        this.applicationData.github = `github.com/${this.applicationData.github}`;
      }
      this.applicationData.linkedin = `linkedin.com/in/${this.applicationData.linkedin}`;

      console.log('Applications Data', this.applicationData);

      await applicationService
        .createApplication(this.applicationData)
        .then(() => {
          // Redirect to SuccessfulSubmission with dynamic query parameters
          this.$router.push({
            path: '/successful-submission',
            query: {
              name: this.applicationData.name || 'Applicant',
              secondTitle: 'for Submitting Your Information!',
              imageSrc: require('@/assets/img/mailsubmit.svg'), // Use a relative path instead of require
              submissionMessage:
                "We've received your details. Our talent acquisition team will review them shortly and get in touch with you if there's a potential fit.",
              ctaText:
                'In the meantime, feel free to explore more opportunities',
              ctaHighlight: 'Talents on Demand',
              buttonText: 'Explore more',
              buttonLink: '/talents-on-demand',
            },
          });
        })
        .catch(() => {
          window.location.reload();
        });
      this.submitting = false;
    },
    validateForm() {
      Object.keys(this.applicationError).forEach((key) => {
        this.applicationError[key] = false;
        if (this.applicationErrorMessage[key]) {
          this.applicationErrorMessage[key] = [];
        }
      });

      let hasErrors = false;

      if (!this.applicationData.name) {
        this.applicationError.name = true;
        this.applicationErrorMessage.name.push('This Field Is Required');
        hasErrors = true;
      }

      if (!this.applicationData.email) {
        this.applicationError.email = true;
        this.applicationErrorMessage.email.push('This Field Is Required');
        hasErrors = true;
      }

      if (!this.applicationData.phone) {
        this.applicationError.phone = true;
        this.applicationErrorMessage.phone.push('This Field Is Required');
        hasErrors = true;
      }
      if (!this.applicationData.location) {
        this.applicationData.location = '';
      }
      if (!this.applicationData.portfolio) {
        this.applicationData.portfolio = '';
      }
      if (!this.applicationData.github) {
        this.applicationData.github = '';
      }

      if (!this.applicationData.linkedin) {
        this.applicationError.linkedin = true;
        this.applicationErrorMessage.linkedin.push('This Field Is Required');
        hasErrors = true;
      }
      if (!this.applicationData.cv || this.applicationData.cv.length === 0) {
        this.applicationError.cv = true;
        this.applicationErrorMessage.cv.push('This Field Is Required');
        hasErrors = true;
      } else {
        // Validate file size during form submission as well
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        const file = this.applicationData.cv[0];
        if (file && file.size > maxSize) {
          this.applicationError.cv = true;
          this.applicationErrorMessage.cv.push('File size must be less than 5MB');
          hasErrors = true;
        }
      }
      if (!this.applicationData.verified) {
        this.applicationError.verified = true;
        this.UIController.snackbars.captchaSnackBar = true;
        hasErrors = true;
      }

      if (!hasErrors) {
        this.submitForm();
      }
    },
  },
  async created() {
    document.getElementById('loaderSection').classList.add('d-none');
    this.UIController.loader.contentLoading = true;
    if (this.$route.query.jobId) {
      this.UIController.displayController.hasQueryVariable = true;
      await jobOpeningService
        .showJobOpenings(this.$route.query.jobId)
        .then((response) => {
          console.log(response.data.name);
          if (response.status === 200) {
            this.applicationData.role = response.data.name;
          }
        });
    }
    const optionsResponse = await applicationService
      .getApplicationOptions()
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        console.log(error);
        return {
          languages: [],
          frameworks: [],
          softwares: [],
          certifications: [],
        };
      });
    console.log('optionsResponse', optionsResponse);

    this.languages = optionsResponse.languages;
    this.frameworks = optionsResponse.frameworks;
    this.softwares = optionsResponse.softwares;
    this.certifications = optionsResponse.certifications;

    this.UIController.loader.contentLoading = false;
  },
};
</script>

<style scoped>
.error-card {
  background-color: var(--light-1);
  max-width: 500px;
  margin-inline: auto;
  margin-block: 2rem;
}

.loader {
  padding-block: 5rem;
}

.form-title {
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

.form__container {
  margin-block: 5rem;
}

.job-application {
  max-width: 900px;
  margin-inline: auto;
}

.form-btn {
  letter-spacing: 0px;
  text-transform: capitalize;
  background-color: var(--primary-color-lighten);
  color: var(--light-1);
  margin-block: 1rem;
}
</style>